import { baseApi, apiTags } from "../baseApi";
import type {
    Client,
    ClientListParams,
    UpdateClientFieldParams,
    BindClientParams,
    UserFinanceGroupParams,
} from "../types/api.types";

export const clientsApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        getClients: builder.query<Client[], ClientListParams>({
            query: ({ clientId, isArchive, sortId: _sortId, isFullGroup = false }) => ({
                resource: "client",
                action: "index",
                method: "GET",
                useAll: true,
                /* eslint-disable @typescript-eslint/no-explicit-any */
                queryBuilder: (query: any) => {
                    query
                        .with("atonUsers")
                        .with("multiple_managers_pivot.manager")
                        .with("mainContact", (contactQuery: any) => {
                            contactQuery
                                .with("refuseReason")
                                .with("tasks", (tasksQuery: any) =>
                                    tasksQuery.orderBy("end_at", "DESC")
                                );
                        })
                        .with("multiple_managers");

                    if (isFullGroup) {
                        query
                            .with("financeGroups")
                            .with("financeGroups.unionUser")
                            .with("oppositeFinanceGroup");
                    }

                    query.whereHas("mainContact", (contactQuery: any) => {
                        contactQuery.where("is_archive", isArchive ? 1 : 0);
                    });

                    if (clientId) {
                        query.where("id", clientId);
                    }

                    return query;
                },
                /* eslint-enable @typescript-eslint/no-explicit-any */
            }),
            providesTags: (result) =>
                result && Array.isArray(result)
                    ? [
                          { type: apiTags.Client, id: "LIST" },
                          ...result.map((item) => ({ type: apiTags.Client, id: item.id })),
                      ]
                    : [{ type: apiTags.Client, id: "LIST" }],
        }),
        updateClientField: builder.mutation<Client, UpdateClientFieldParams>({
            query: ({ clientId, params }) => ({
                resource: "client",
                action: "update-field",
                method: "PUT",
                id: clientId,
                data: params,
            }),
            invalidatesTags: (result, error, arg) => [
                { type: apiTags.Client, id: arg.clientId },
                { type: apiTags.Client, id: "LIST" },
            ],
        }),
        bindClient: builder.mutation<unknown, BindClientParams>({
            query: ({ accountsToBind, queryParamsList, shareUser: _shareUser = false }) => ({
                resource: "client",
                action: "bind",
                method: "POST",
                data:
                    accountsToBind.length > 0
                        ? { ...queryParamsList, services: accountsToBind }
                        : queryParamsList,
            }),
            invalidatesTags: [{ type: apiTags.Client, id: "LIST" }],
        }),
        getClientTransitions: builder.query<unknown[], void>({
            query: () => ({
                resource: "crm-client-transition",
                action: "index",
                method: "GET",
            }),
            providesTags: [{ type: apiTags.Client, id: "TRANSITIONS" }],
        }),
        getUserFinanceGroup: builder.query<unknown[], UserFinanceGroupParams>({
            query: ({ userId }) => ({
                resource: "user-finance-group",
                action: "index",
                method: "GET",
                queryBuilder: (query: import("../baseApi").LaravelQueryBuilder) =>
                    query.with("unionUser").where("user_id", userId),
            }),
            providesTags: (result, error, arg) => [
                { type: apiTags.Client, id: `FINANCE_GROUP_${arg.userId}` },
            ],
        }),
    }),
});

export const {
    useGetClientsQuery,
    useUpdateClientFieldMutation,
    useBindClientMutation,
    useGetClientTransitionsQuery,
    useGetUserFinanceGroupQuery,
} = clientsApi;
